/**
 * THE PHONE AND THE LAPTOP AGREE.
 *
 * A contractor quotes a house from his truck and finishes the paperwork at the
 * kitchen table. Before this, those were two different sets of jobs in two
 * different browsers with no relationship at all, and a cleared browser took a
 * season with it.
 *
 * THE BROWSER IS STILL WHERE THE APP READS AND WRITES. Nothing here sits in
 * front of a keystroke. A driveway in a gated community has no signal, and a
 * quoting tool that needs bars is a quoting tool nobody opens. IndexedDB is
 * the working copy; this pushes it up afterwards and pulls down what other
 * devices did.
 *
 * NEWER WINS, PER JOB, AND TWO DESIGNS ARE NEVER MERGED. Nobody edits the same
 * house on two devices in the same minute. What must never happen is quietly
 * overwriting work that has not been sent, so an unsent local copy always
 * refuses an incoming one.
 *
 * THE THUMBNAIL IS REMADE, NOT SENT. It is derived from the photograph, and
 * the photograph is already crossing. Sending it too would put fifteen
 * kilobytes of duplicate picture in every row for no reason; not making it at
 * all leaves a second device showing a grid of blank gray tiles, which is what
 * the job list is read by.
 *
 * THE PHOTOGRAPH GOES SEPARATELY AND ONCE. It is two to three megabytes and
 * changes when the job gets it; the design beside it is a few dozen kilobytes
 * and changes on every price typed. Sent together, every keystroke would drag
 * the photo behind it, which on a phone reads as the app being broken.
 */

import { supa } from './supa'
import {
  acceptFromServer,
  claimOrphans,
  listUnsent,
  localIndex,
  forgetJob,
  makeThumb,
  markSent,
  photoOf,
  putPhoto,
  setThumb,
  type StoredDoc,
} from './db'
import { loadCompany, saveCompany, type Company } from './company'
import { readSticky, writeSticky } from '../store/useEditor'

const BUCKET = 'job-photos'

/** What one pass did, so the interface can say something true about it. */
export interface SyncResult {
  pushed: number
  pulled: number
  claimed: number
  failed: number
  offline: boolean
}

const empty = (): SyncResult => ({ pushed: 0, pulled: 0, claimed: 0, failed: 0, offline: false })

/* --------------------------------------------------------------- the photo */

const photoPath = (userId: string, jobId: string) => `${userId}/${jobId}`

/**
 * A data URL is what the app holds and a Blob is what storage takes.
 *
 * Done by hand rather than with fetch(dataUrl), which works but allocates the
 * whole thing twice and is measurably slow on a phone with a three megabyte
 * photograph.
 */
function dataUrlToBlob(dataUrl: string): Blob | null {
  const comma = dataUrl.indexOf(',')
  if (comma < 0) return null
  const header = dataUrl.slice(0, comma)
  const type = /data:([^;]+)/.exec(header)?.[1] ?? 'image/jpeg'
  if (!/;base64/.test(header)) return null
  try {
    const bin = atob(dataUrl.slice(comma + 1))
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    return new Blob([bytes], { type })
  } catch {
    return null
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(r.error)
    r.readAsDataURL(blob)
  })
}

/**
 * Put the photograph up if it is not already there.
 *
 * upsert is false on purpose. A photo does not change once a job has one, and
 * re-uploading three megabytes on every sync pass is exactly the bill nobody
 * notices until the invoice arrives. An existing object answers with a
 * conflict, which is the success case here, not a failure.
 */
async function pushPhoto(userId: string, jobId: string): Promise<void> {
  const dataUrl = await photoOf(jobId)
  if (!dataUrl) return
  const blob = dataUrlToBlob(dataUrl)
  if (!blob) return
  const { error } = await supa.storage
    .from(BUCKET)
    .upload(photoPath(userId, jobId), blob, { contentType: blob.type, upsert: false })
  // 409 is "already there", which is the whole point of not re-uploading.
  if (error && !/exists|duplicate|409/i.test(error.message)) throw error
}

/**
 * Fetch a photograph this browser does not have yet, and rebuild its thumbnail.
 *
 * The thumbnail is not in the row on purpose - it is derived from the photo,
 * and the photo is already crossing - so it has to be remade on arrival or the
 * job list on a second device is a grid of blank gray tiles. That list is
 * recognized by its pictures and by nothing else.
 *
 * Missing is not an error at any step. A job with no photograph is ordinary,
 * and a thumbnail that fails to render leaves a job that still opens.
 */
async function pullPhoto(userId: string, jobId: string): Promise<void> {
  const have = await photoOf(jobId)
  const dataUrl =
    have ??
    (await (async () => {
      const { data, error } = await supa.storage.from(BUCKET).download(photoPath(userId, jobId))
      if (error || !data) return null
      try {
        const url = await blobToDataUrl(data)
        await putPhoto(jobId, url)
        return url
      } catch {
        /* a full quota. The job still opens, without its picture. */
        return null
      }
    })())

  if (!dataUrl) return
  try {
    const thumb = await makeThumb(dataUrl)
    if (thumb) await setThumb(jobId, thumb)
  } catch {
    /* a blank tile is a great deal better than a job that would not load */
  }
}

/* ------------------------------------------------------------- the columns */

/**
 * A stored job as a row.
 *
 * The design goes in whole, as one document, because nothing queries inside
 * it. The four columns beside it are its shadow: they exist so the job list
 * and the customer screen are one query rather than forty parsed documents,
 * and they are written from the document every time so they cannot drift.
 */
function rowFor(d: StoredDoc, userId: string) {
  /*
   * WHAT DOES NOT NEED TO TRAVEL.
   *
   * thumb is derived from the photograph and the photograph is already
   * crossing, so sending it too would be fifteen kilobytes of duplicate
   * picture in every row. It is remade on arrival.
   *
   * pricing is the old derived quote's rate card, season tier, line overrides
   * and extras. The quote is typed now - lib/quote.ts multiplies what somebody
   * entered and derives nothing - so nothing in a shipped build reads it. It
   * stays in the local document because removing the field is a migration, and
   * it stops here because otherwise every job carries four hundred dead bytes
   * to the server and back on every single save, forever. Across a season of a
   * few hundred contractors that is real storage paid for nothing.
   *
   * dirty and syncedAt are this browser's bookkeeping and mean nothing to
   * anybody else's.
   */
  const {
    thumb: _thumb,
    dirty: _dirty,
    syncedAt: _synced,
    pricing: _pricing,
    ...doc
  } = d
  return {
    id: d.id,
    account_id: userId,
    doc,
    name: d.name || 'Untitled mockup',
    customer: d.customer ?? '',
    address: d.address ?? '',
    status: d.status ?? 'draft',
    money: d.money ?? null,
    updated_at: d.savedAt,
    // A delete travels as a column, not as an absence. See deleteProject.
    deleted_at: d.deletedAt ?? null,
  }
}

/** A row coming back the other way. */
function storedFrom(row: Record<string, unknown>): StoredDoc | null {
  const doc = row.doc as StoredDoc | undefined
  if (!doc || typeof doc !== 'object') return null
  return {
    ...doc,
    // The server's time is the truth about when this version happened; a
    // device clock that is a few minutes out must not win a comparison.
    savedAt: String(row.updated_at ?? doc.savedAt),
    dirty: false,
    syncedAt: String(row.updated_at ?? ''),
  }
}

/* ------------------------------------------------------------- the passes */

/** Everything unsent, up. Photographs first, so a row never names a missing one. */
async function push(userId: string): Promise<{ pushed: number; failed: number }> {
  const unsent = await listUnsent()
  let pushed = 0
  let failed = 0
  for (const d of unsent) {
    try {
      // A deleted job has no photograph left to send.
      if (!d.deletedAt) await pushPhoto(userId, d.id)
      const { error } = await supa.from('jobs').upsert(rowFor(d, userId), { onConflict: 'id' })
      if (error) throw error
      await markSent(d.id, d.savedAt, d.savedAt)
      // The server has been told. Nothing local needs to remember it any more.
      if (d.deletedAt) await forgetJob(d.id)
      pushed++
    } catch {
      // One job failing does not abandon the rest, and it stays dirty, so the
      // next pass tries again. That is the whole of the retry policy.
      failed++
    }
  }
  return { pushed, failed }
}

/** Everything newer, down. */
async function pull(userId: string): Promise<number> {
  const { data, error } = await supa
    .from('jobs')
    .select('id, doc, updated_at, deleted_at')
    .order('updated_at', { ascending: false })
    .limit(500)
  if (error || !data) return 0

  const mine = await localIndex()
  let pulled = 0
  for (const row of data as Record<string, unknown>[]) {
    const id = String(row.id)
    const stamp = String(row.updated_at ?? '')
    const here = mine.get(id)

    /*
     * DELETED SOMEWHERE ELSE.
     *
     * The rows are fetched including tombstones rather than filtered out,
     * because a job deleted on the phone has to disappear from the laptop too.
     * Filtering them away server-side made a delete look like a job that was
     * simply never mentioned, and the laptop kept it forever.
     *
     * Unsent local work still wins: somebody who drew on this device since is
     * not overruled by a deletion from another one.
     */
    if (row.deleted_at) {
      if (here && !here.dirty) await forgetJob(id)
      continue
    }
    // Nothing to do for a job this browser already has at the same version.
    if (here && !here.dirty && here.savedAt >= stamp) continue
    const stored = storedFrom(row)
    if (!stored) continue
    const what = await acceptFromServer(stored)
    if (what === 'wrote') {
      await pullPhoto(userId, id)
      pulled++
    }
  }
  return pulled
}

/**
 * The letterhead, the prefix, the quote counter AND the light defaults.
 *
 * The defaults - last color, spacing, bulb size, which product each category
 * uses - were the last thing in this app that stayed in one browser. Sign in
 * on a laptop and everything you had set came back at factory settings.
 * Nothing was lost, it simply did not follow you, which is the same complaint
 * in a smaller coat.
 *
 * One row, and the newer of the two wins whole. Merging field by field would
 * let a stale device put back a logo somebody deleted on another one.
 *
 * The counter is the exception and is taken with max(), never overwritten
 * downward: two devices each numbering from 1042 is a quote book with two
 * LP-1042s in it, which somebody has to explain to a customer.
 */
async function syncSettings(): Promise<void> {
  const local = loadCompany()
  const { data } = await supa
    .from('settings')
    .select('data, next_number, updated_at')
    .maybeSingle()

  const remote = (data?.data ?? null) as Company | null
  const remoteNumber = Number(data?.next_number ?? 0)
  const merged: Company = remote && !localHasMore(local, remote) ? { ...remote } : { ...local }
  merged.nextNumber = Math.max(local.nextNumber || 1001, remoteNumber || 1001)
  saveCompany(merged)

  /*
   * The defaults come down only when this browser has none of its own worth
   * keeping. A device somebody has actually set up is not overwritten by
   * whatever another one happened to be left on.
   */
  const localSticky = readSticky()
  const remoteSticky = (data?.data as { sticky?: unknown } | null)?.sticky
  const untouched = !localStorage.getItem('lightsos.defaults.v1')
  if (untouched && remoteSticky) writeSticky(remoteSticky as ReturnType<typeof readSticky>)

  const { nextNumber: _n, ...rest } = merged
  await supa.from('settings').upsert(
    {
      account_id: (await supa.auth.getUser()).data.user?.id,
      data: { ...rest, nextNumber: merged.nextNumber, sticky: untouched ? remoteSticky : localSticky },
      next_number: merged.nextNumber,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'account_id' },
  )
}

/**
 * Whether this browser's letterhead is worth more than the server's.
 *
 * A blank one never wins. The failure this prevents is a fresh browser, signed
 * in for the first time with nothing typed into it, pushing an empty
 * letterhead over the real one and taking somebody's logo off their quotes.
 */
function localHasMore(local: Company, remote: Company): boolean {
  const filled = (c: Company) =>
    [c.name, c.phone, c.email, c.address, c.license, c.prefix].filter((v) => v.trim()).length +
    (c.logoDataUrl ? 2 : 0)
  return filled(local) > filled(remote)
}

/* ---------------------------------------------------------------- the door */

let running: Promise<SyncResult> | null = null

/**
 * One pass, and never two at once.
 *
 * Sync is triggered by a save, by coming back to the tab and by signing in,
 * and those arrive together constantly. Two passes racing would push the same
 * job twice and, worse, have one clear a dirty flag the other had just set.
 * Callers get the pass already in flight.
 */
export function syncNow(userId: string | undefined): Promise<SyncResult> {
  if (!userId) return Promise.resolve(empty())
  if (running) return running
  running = (async () => {
    const out = empty()
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      out.offline = true
      return out
    }
    try {
      out.claimed = await claimOrphans(userId)
      const p = await push(userId)
      out.pushed = p.pushed
      out.failed = p.failed
      out.pulled = await pull(userId)
      await syncSettings()
    } catch {
      // The network, not the person. Everything unsent stays unsent and the
      // next pass picks it up.
      out.failed++
    }
    return out
  })().finally(() => {
    running = null
  })
  return running
}
